/**
 * Lucy Upsell — two-modal system
 *
 * Hat count in cart → which modal to show:
 *   = 1 hat              → Deal modal  ("3 hats for $40")
 *   ≥ 2 hats, total < $50 → Free shipping modal
 *   ≥ 2 hats, total ≥ $50 → nothing
 *   0 hats               → nothing
 */

const FSU_THRESHOLD   = 5000;  // $50 free-shipping threshold (cents)
const FSU_COOLDOWN_MS = 8000;  // ms before popup can re-show after dismiss

// All hat variant IDs on the store
const HAT_VARIANT_IDS = new Set([52653519864173, 52653519896941]);

// Deal button: add 1× Lucky Branded + 1× Bucket Hat → brings total to 3
const FSU_DEAL_ITEMS = [
  { id: 52653519864173, quantity: 1 }, // Lucky Branded
  { id: 52653519896941, quantity: 1 }, // Bucket Hat
];

// Free shipping button: add 1× Bucket Hat → pushes total over $50
const FSU_SHIPPING_ITEMS = [
  { id: 52653519896941, quantity: 1 }, // Bucket Hat
];

const FSU_UTM         = '?utm_source=lucy_popup&utm_medium=upsell&utm_campaign=free_shipping_upsell';
const FSU_SESSION_KEY = 'lucy_upsell_clicked';

let fsuLastDismissed    = 0;
let fsuAddingInProgress = false;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fsuFormatMoney(cents) {
  const d = cents / 100;
  return '$' + (d % 1 === 0 ? d.toFixed(0) : d.toFixed(2));
}

function countHats(cart) {
  return (cart.items || []).reduce((sum, item) => {
    return HAT_VARIANT_IDS.has(item.variant_id) ? sum + item.quantity : sum;
  }, 0);
}

// Single atomic add — sends all items in one request.
async function fsuAddToCart(items) {
  const res = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.description || `Cart add failed (${res.status})`);
  }
  return res.json();
}

// Refresh the cart drawer after an add — mirrors Dawn's own CartDrawerItems.onCartUpdate().
// Fetches fresh section HTML and replaces the relevant DOM nodes in place.
function fsuRenderCart() {
  // Prefer Dawn's native method (same fetch Dawn uses for quantity changes)
  const cartDrawerItems = document.querySelector('cart-drawer-items');
  if (cartDrawerItems && typeof cartDrawerItems.onCartUpdate === 'function') {
    cartDrawerItems.onCartUpdate();
    return;
  }
  // Fallback: replicate the same fetch manually
  const cartUrl = (typeof routes !== 'undefined' && routes.cart_url) ? routes.cart_url : '/cart';
  fetch(`${cartUrl}?section_id=cart-drawer`)
    .then((r) => r.text())
    .then((text) => {
      const html = new DOMParser().parseFromString(text, 'text/html');
      ['cart-drawer-items', '.cart-drawer__footer'].forEach((sel) => {
        const target = document.querySelector(sel);
        const source = html.querySelector(sel);
        if (target && source) target.replaceWith(source);
      });
    })
    .catch((e) => console.error('[Lucy] Cart refresh failed:', e));
}

async function fsuTagCart(attributes) {
  await fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ attributes }),
  });
}

// ─── Open / close ─────────────────────────────────────────────────────────────

window.fsuCloseAll = function () {
  document.getElementById('fsu-deal-modal')?.setAttribute('hidden', '');
  document.getElementById('free-shipping-modal')?.setAttribute('hidden', '');
  fsuLastDismissed = Date.now();
};
window.fsuClose = window.fsuCloseAll;

function fsuCooldownActive() {
  return (Date.now() - fsuLastDismissed) < FSU_COOLDOWN_MS;
}

// ─── Deal modal ───────────────────────────────────────────────────────────────

function fsuShowDeal() {
  document.getElementById('free-shipping-modal')?.setAttribute('hidden', '');
  document.getElementById('fsu-deal-modal')?.removeAttribute('hidden');
}

window.fsuGetDeal = async function (btn) {
  if (btn.disabled) return;
  btn.disabled = true;
  btn.textContent = 'Adding…';
  fsuAddingInProgress = true;

  try {
    // Add 1× Lucky Branded + 1× Bucket Hat in one request
    await fsuAddToCart(FSU_DEAL_ITEMS);
    await fsuTagCart({ lucy_deal_clicked: 'true', lucy_deal_clicked_at: new Date().toISOString() });

    btn.textContent = '✓ Added!';
    setTimeout(() => {
      fsuAddingInProgress = false;
      window.fsuCloseAll();
      fsuRenderCart();
    }, 900);

  } catch (err) {
    fsuAddingInProgress = false;
    btn.disabled = false;
    btn.textContent = 'Get the deal →';
    console.error('[Lucy upsell] Deal add failed:', err.message);
  }
};

// ─── Free shipping modal ──────────────────────────────────────────────────────

function fsuUpdateShippingModal(cartTotal) {
  const modal = document.getElementById('free-shipping-modal');
  if (!modal) return;

  const remaining = FSU_THRESHOLD - cartTotal;
  const progress  = Math.min((cartTotal / FSU_THRESHOLD) * 100, 100);

  const heading = modal.querySelector('.fsu-heading');
  const subtext = modal.querySelector('.fsu-subtext');
  const barFill = modal.querySelector('#fsu-bar-fill');
  const barCur  = modal.querySelector('#fsu-bar-current');

  if (heading) heading.textContent = 'So close to free shipping!';
  if (subtext) subtext.innerHTML = `Add <strong>${fsuFormatMoney(remaining)}</strong> more to your order and we'll ship it free.`;
  if (barFill) { barFill.style.width = progress + '%'; barFill.classList.remove('fsu-bar-fill--complete'); }
  if (barCur)  barCur.textContent = fsuFormatMoney(cartTotal);
  modal.dataset.cartTotal = cartTotal;
}

function fsuShowShipping(cartTotal) {
  document.getElementById('fsu-deal-modal')?.setAttribute('hidden', '');
  fsuUpdateShippingModal(cartTotal);
  document.getElementById('free-shipping-modal')?.removeAttribute('hidden');
}

window.fsuGetFreeShipping = async function (btn) {
  if (btn.disabled) return;
  btn.disabled = true;
  btn.textContent = 'Adding…';
  fsuAddingInProgress = true;

  try {
    // Add 1× Bucket Hat in one request
    await fsuAddToCart(FSU_SHIPPING_ITEMS);
    await fsuTagCart({ lucy_upsell_clicked: 'true', lucy_upsell_clicked_at: new Date().toISOString() });

    sessionStorage.setItem(FSU_SESSION_KEY, '1');
    if (window.Shopify?.analytics?.publish) {
      window.Shopify.analytics.publish('lucy_upsell_button_clicked', {
        cart_total_cents: parseInt(document.getElementById('free-shipping-modal')?.dataset?.cartTotal || 0),
        threshold_cents: FSU_THRESHOLD,
      });
    }

    btn.textContent = '✓ Added!';
    fsuApplyCheckoutUtm();

    setTimeout(() => {
      fsuAddingInProgress = false;
      window.fsuCloseAll();
      fsuRenderCart();
    }, 900);

  } catch (err) {
    fsuAddingInProgress = false;
    btn.disabled = false;
    btn.textContent = 'Get free shipping';
    console.error('[Lucy upsell] Shipping add failed:', err.message);
  }
};

// ─── UTM tagging ──────────────────────────────────────────────────────────────

function fsuApplyCheckoutUtm() {
  const checkoutLink = document.querySelector('#free-shipping-modal .fsu-btn--primary');
  if (checkoutLink && !checkoutLink.href.includes('utm_campaign')) {
    checkoutLink.href = '/checkout' + FSU_UTM;
  }
  const cartForm = document.getElementById('CartDrawer-Form');
  if (cartForm && !cartForm.dataset.fsuTagged) {
    cartForm.dataset.fsuTagged = '1';
    cartForm.addEventListener('submit', (e) => {
      if (sessionStorage.getItem(FSU_SESSION_KEY)) {
        e.preventDefault();
        window.location.href = '/checkout' + FSU_UTM;
      }
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (sessionStorage.getItem(FSU_SESSION_KEY)) fsuApplyCheckoutUtm();
});

// ─── Core decision logic ──────────────────────────────────────────────────────

function fsuDecide(cart) {
  if (fsuAddingInProgress) return;
  if (fsuCooldownActive())  return;

  const hats  = countHats(cart);
  const total = cart.total_price;

  if (hats === 1) {
    fsuShowDeal();
  } else if (hats >= 2 && total < FSU_THRESHOLD) {
    fsuShowShipping(total);
  }
}

// ─── Intercept cart AJAX calls ────────────────────────────────────────────────

(function interceptCartFetch() {
  const _fetch = window.fetch;
  window.fetch = async function (...args) {
    const response = await _fetch.apply(this, args);
    const url = (typeof args[0] === 'string' ? args[0] : args[0]?.url) || '';
    const isCartMutation =
      url.includes('/cart/add') ||
      url.includes('/cart/change') ||
      url.includes('/cart/update');

    if (isCartMutation) {
      setTimeout(async () => {
        try {
          const r = await _fetch('/cart.js');
          fsuDecide(await r.json());
        } catch (_) {}
      }, 300);
    }
    return response;
  };
})();

// ─── Dawn theme pubsub hook ───────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', () => {
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, async () => {
      try {
        const r = await fetch('/cart.js');
        fsuDecide(await r.json());
      } catch (_) {}
    });
  }
});

// ─── Close on overlay click / Escape ─────────────────────────────────────────

document.addEventListener('click', (e) => {
  if (e.target?.classList?.contains('fsu-overlay')) window.fsuCloseAll();
});

document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') window.fsuCloseAll();
});
